import { useEffect, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Divider, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { SimplePaletteColorOptions, TypeBackground, TypeText } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { Formik, useFormikContext } from 'formik';
import {
  getGetFeatureConfigsPublicQueryKey,
  getGetFeatureConfigsQueryKey,
  useCreateFeatureConfig,
  useUpdateSiteFeatureConfig,
} from '@juno/client-api';
import { FeatureConfig as FeatureConfigModel, Site as SiteModel } from '@juno/client-api/model';
import { FeatureConfig } from '@juno/client-api/utils';
import {
  DEFAULT_THEME_CONFIG_PRESET,
  THEME_FONT_FAMILIES,
  ThemeConfigProps,
} from '@juno/constants';
import { MutationAction, onMutation } from '@juno/utils';
import ColorPicker from './ColorPicker';
import { SiteDesignContent, SiteDesignContentPanel } from './styles';

type FormValues = Omit<ThemeConfigProps, 'palette'> & {
  palette: {
    primary: SimplePaletteColorOptions;
    secondary: SimplePaletteColorOptions;
    background: TypeBackground;
    text: Partial<TypeText>;
    info: SimplePaletteColorOptions;
  };
};

export const mergeWithDefaults = (themeConfig: ThemeConfigProps): FormValues => {
  return {
    siteTitle: themeConfig?.siteTitle ?? DEFAULT_THEME_CONFIG_PRESET.siteTitle,
    name: themeConfig?.name ?? DEFAULT_THEME_CONFIG_PRESET.name,
    slug: themeConfig?.slug ?? DEFAULT_THEME_CONFIG_PRESET.slug,
    typography: {
      fontFamily:
        themeConfig?.typography?.fontFamily ?? DEFAULT_THEME_CONFIG_PRESET.typography.fontFamily,
    },
    palette: {
      primary: {
        main:
          (themeConfig?.palette?.primary as SimplePaletteColorOptions)?.main ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.primary as SimplePaletteColorOptions).main,
        contrastText:
          (themeConfig?.palette?.primary as SimplePaletteColorOptions)?.contrastText ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.primary as SimplePaletteColorOptions).contrastText,
      },
      secondary: {
        main:
          (themeConfig?.palette?.secondary as SimplePaletteColorOptions)?.main ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.secondary as SimplePaletteColorOptions).main,
        contrastText:
          (themeConfig?.palette?.secondary as SimplePaletteColorOptions)?.contrastText ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.secondary as SimplePaletteColorOptions).contrastText,
      },
      background: {
        default:
          themeConfig?.palette?.background?.default ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.background as TypeBackground).default,
        paper:
          themeConfig?.palette?.background?.paper ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.background as TypeBackground).paper,
      },
      text: {
        primary:
          themeConfig?.palette?.text?.primary ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.text as TypeText).primary,
        secondary:
          themeConfig?.palette?.text?.secondary ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.text as TypeText).secondary,
      },
      info: {
        main:
          (themeConfig?.palette?.info as SimplePaletteColorOptions)?.main ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.info as SimplePaletteColorOptions).main,
        contrastText:
          (themeConfig?.palette?.info as SimplePaletteColorOptions)?.contrastText ??
          (DEFAULT_THEME_CONFIG_PRESET.palette.info as SimplePaletteColorOptions).contrastText,
      },
    },
  };
};

interface SiteDesignPanelInnerProps {
  setUnsavedChanges?: (value: boolean) => void;
  isSaving: boolean;
  isDefaultTheme: boolean;
}

const SiteDesignPanelInner = ({
  setUnsavedChanges,
  isSaving,
  isDefaultTheme,
}: SiteDesignPanelInnerProps) => {
  const { values, dirty, setFieldValue, isValid, handleSubmit, getFieldMeta, errors } =
    useFormikContext<FormValues>();

  useEffect(() => {
    setUnsavedChanges?.(dirty);
  }, [dirty, setUnsavedChanges]);

  const getNewPaletteFromColor = (color: string, colorName: string, values: FormValues) => {
    const palette = values.palette;

    const [type, key] = colorName.split('.');
    const newPalette = {
      ...palette,
      [type]: {
        ...(palette[type as keyof typeof palette] as any),
        [key]: color,
      },
    };

    return newPalette;
  };

  return (
    <SiteDesignContent>
      <SiteDesignContentPanel>
        <Grid container>
          <Grid item xs={6} pb={2}>
            <Typography variant='subtitle1'>Site Title</Typography>
            <Typography variant='body2'>
              The document title that appears in browser tabs for your site
            </Typography>
          </Grid>
          <Grid item xs={6} pb={2} justifyContent={'flex-end'} display={'flex'}>
            <TextField
              label='Site Title'
              value={values.siteTitle || ''}
              onChange={(e) => setFieldValue('siteTitle', e.target.value)}
              size='small'
              disabled={isSaving}
              fullWidth
              error={!!getFieldMeta('siteTitle').error}
              helperText={errors.siteTitle}
              sx={{ width: 170 }}
            />
          </Grid>
          <Grid item xs={6} py={2}>
            <Typography variant='subtitle1'>Font</Typography>
            <Typography variant='body2'>
              Select the font for text that appears on the site
            </Typography>
          </Grid>
          <Grid item xs={6} py={2} justifyContent={'flex-end'} display={'flex'}>
            <TextField
              select
              label='Theme Font'
              value={values.typography?.fontFamily || ''}
              onChange={(e) => setFieldValue('typography.fontFamily', e.target.value)}
              size='small'
              disabled={isSaving}
              fullWidth
              sx={{ width: 170 }}
            >
              {THEME_FONT_FAMILIES.map((o) => (
                <MenuItem key={o.id} value={o.value}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} py={2}>
            <Typography variant='subtitle1'>Primary color</Typography>
            <Typography variant='body2'>
              Affects buttons, highlighted menu items, loading spinners, other accents and is
              available to use while building pages or library content
            </Typography>
          </Grid>
          <Grid item xs={6} py={2} justifyContent={'flex-end'} display={'flex'}>
            <ColorPicker
              label='Primary'
              color={values.palette.primary.main}
              colorName='primary.main'
              handleChange={(color, colorName) => {
                const newPalette = getNewPaletteFromColor(color, colorName, values);
                setFieldValue('palette', newPalette);
              }}
              disabled={isSaving}
            />
          </Grid>
          <Divider />
          <Grid item xs={6} py={2}>
            <Typography variant='subtitle1'>Secondary color</Typography>
            <Typography variant='body2'>
              Available to use while building pages or library content
            </Typography>
          </Grid>
          <Grid item xs={6} md={6} py={2} justifyContent={'flex-end'} display={'flex'}>
            <ColorPicker
              label='Secondary'
              color={values.palette.secondary.main}
              colorName='secondary.main'
              handleChange={(color, colorName) => {
                const newPalette = getNewPaletteFromColor(color, colorName, values);
                setFieldValue('palette', newPalette);
              }}
              disabled={isSaving}
            />
          </Grid>
          <Grid item xs={6} py={2}>
            <Typography variant='subtitle1'>Accent color</Typography>
            <Typography variant='body2'>
              Available to use while building pages or library content
            </Typography>
          </Grid>
          <Grid item xs={6} py={2} justifyContent={'flex-end'} display={'flex'}>
            <ColorPicker
              label='Accent'
              color={values.palette.info.main}
              colorName='info.main'
              handleChange={(color, colorName) => {
                const newPalette = getNewPaletteFromColor(color, colorName, values);
                setFieldValue('palette', newPalette);
              }}
              disabled={isSaving}
            />
          </Grid>
        </Grid>
        {isDefaultTheme ? (
          <LoadingButton
            variant='contained'
            aria-label='Save Site Theme'
            onClick={() => handleSubmit()}
            loading={isSaving}
            sx={{ marginLeft: 'auto' }}
          >
            Save
          </LoadingButton>
        ) : (
          <LoadingButton
            variant='contained'
            aria-label='Save Site Theme'
            onClick={() => handleSubmit()}
            loading={isSaving}
            disabled={!dirty || !isValid}
            sx={{ marginLeft: 'auto' }}
          >
            Save
          </LoadingButton>
        )}
      </SiteDesignContentPanel>
    </SiteDesignContent>
  );
};

interface SiteDesignPanelProps {
  site: SiteModel;
  configs: FeatureConfigModel[] | undefined;
  setUnsavedChanges?: (unsavedChanges: boolean) => void;
}
const SiteDesignPanel = ({ configs, site, setUnsavedChanges }: SiteDesignPanelProps) => {
  const [isSaving, setIsSaving] = useState(false);

  const themeConfig = FeatureConfig.getThemeConfigTheme(configs);
  const isDefaultTheme = !themeConfig;
  const initialValues = mergeWithDefaults(themeConfig);

  const queryClient = useQueryClient();
  const refetchConfigs = () => {
    const queryKey = getGetFeatureConfigsQueryKey(site.id);
    const publicQueryKey = getGetFeatureConfigsPublicQueryKey(site.id);
    queryClient.invalidateQueries({ queryKey: queryKey });
    queryClient.invalidateQueries({ queryKey: publicQueryKey });
  };

  const configCreate = useCreateFeatureConfig(
    onMutation(MutationAction.CREATE, 'FeatureConfig', refetchConfigs),
  );
  const configUpdate = useUpdateSiteFeatureConfig(
    onMutation(MutationAction.UPDATE, 'FeatureConfig', refetchConfigs),
  );

  const submitThemeConfig = async (values: FormValues) => {
    setIsSaving(true);

    const themeConfigUpdate = {
      ...values,
      palette: values.palette as ThemeConfigProps['palette'],
    };

    const data = FeatureConfig.buildThemePayload(themeConfigUpdate, configs);
    try {
      if (data.id) {
        await configUpdate.mutateAsync({ siteId: site.id, configType: data.type, data });
      } else {
        await configCreate.mutateAsync({ siteId: site.id, data });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const validateThemeConfig = (values: FormValues) => {
    const errors: Partial<FormValues> = {};

    if (!!values.siteTitle && values.siteTitle.length > 60) {
      errors.siteTitle = 'Site title must be less than 60 characters';
    }

    return errors;
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitThemeConfig}
      validate={validateThemeConfig}
      enableReinitialize
    >
      <SiteDesignPanelInner
        setUnsavedChanges={setUnsavedChanges}
        isSaving={isSaving}
        isDefaultTheme={isDefaultTheme}
      />
    </Formik>
  );
};

export default SiteDesignPanel;
